import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { logger } from '../logger';
import { createSelectors } from '../selectors';
import { AI_PLANNER_STORE_NAME, STEP_ORDER } from './constants';
import type {
  AIPlannerActions,
  AIPlannerPreferences,
  AIPlannerState,
  MealPlan,
  MealStatus,
  OnboardingStep,
  StreamingMealItem,
} from './types';

const initialState: AIPlannerState = {
  // Persisted to localStorage
  preferences: {},
  hasPlanHistory: false,

  // Runtime state (not persisted)
  currentPlan: null, // Fetched from API, not persisted
  isSheetOpen: false,
  currentStep: 'intro',
  isGenerating: false,
  isRegenerating: false,

  // Streaming state (not persisted)
  streamingText: '',
  streamingProgress: 0,
  streamingMeals: [],
  streamingMessages: [],
};

export const aiPlannerStore = create<
  AIPlannerState & AIPlannerActions,
  [
    ['zustand/devtools', AIPlannerState & AIPlannerActions],
    [
      'zustand/persist',
      Pick<
        AIPlannerState,
        'preferences' | 'hasPlanHistory' | 'streamingMessages' | 'streamingText'
      >,
    ],
  ]
>(
  logger(
    devtools(
      persist(
        (set, get) => ({
          ...initialState,

          // Sheet control
          openSheet: () => set({ isSheetOpen: true }),
          closeSheet: () => {
            set({ isSheetOpen: false });
            // Reset non-persisted state when closing
            setTimeout(() => {
              const state = get();
              if (!state.isSheetOpen) {
                set({
                  currentStep: state.currentPlan ? 'overview' : 'intro',
                  isGenerating: false,
                  isRegenerating: false,
                  // Keep streamingText and streamingMessages to persist across sheet close/open
                  streamingProgress: 0,
                  streamingMeals: [],
                });
              }
            }, 300); // Match animation duration
          },

          // Preferences
          setPreferences: (prefs: Partial<AIPlannerPreferences>) =>
            set((state) => ({
              preferences: { ...state.preferences, ...prefs },
            })),

          clearPreferences: () => set({ preferences: {} }),

          // Step navigation
          setCurrentStep: (step: OnboardingStep) => set({ currentStep: step }),

          nextStep: () => {
            const currentIndex = STEP_ORDER.indexOf(get().currentStep);
            if (currentIndex < STEP_ORDER.length - 1) {
              set({ currentStep: STEP_ORDER[currentIndex + 1] });
            }
          },

          prevStep: () => {
            const currentIndex = STEP_ORDER.indexOf(get().currentStep);
            if (currentIndex > 0) {
              set({ currentStep: STEP_ORDER[currentIndex - 1] });
            }
          },

          // Plan management
          setPlan: (plan: MealPlan) =>
            set({
              currentPlan: plan,
              hasPlanHistory: true,
              currentStep: 'overview',
              isGenerating: false,
              isRegenerating: false,
            }),

          updateMealStatus: (
            dayDate: string,
            mealId: string,
            status: MealStatus
          ) =>
            set((state) => {
              if (!state.currentPlan) return state;

              const updatedPlan = {
                ...state.currentPlan,
                weeks: state.currentPlan.weeks.map((week) => ({
                  ...week,
                  days: week.days.map((day) => {
                    if (day.date !== dayDate) return day;
                    return {
                      ...day,
                      meals: day.meals.map((meal) =>
                        meal.id === mealId ? { ...meal, status } : meal
                      ),
                    };
                  }),
                })),
              };

              return { currentPlan: updatedPlan };
            }),

          clearPlan: () =>
            set({
              currentPlan: null,
              currentStep: 'intro',
            }),

          startRegeneration: () =>
            set({
              currentPlan: null,
              currentStep: 'intro',
              isRegenerating: true,
            }),

          // Streaming
          setIsGenerating: (isGenerating: boolean) => set({ isGenerating }),

          setStreamingProgress: (progress: number) =>
            set({ streamingProgress: progress }),

          appendStreamingText: (text: string) =>
            set((state) => ({
              streamingText: state.streamingText + text,
            })),

          addStreamingMeal: (meal: StreamingMealItem) =>
            set((state) => ({
              streamingMeals: [...state.streamingMeals, meal],
            })),

          setStreamingMessages: (messages: any[]) =>
            set({ streamingMessages: messages }),

          clearStreamingState: () =>
            set({
              streamingText: '',
              streamingProgress: 0,
              streamingMeals: [],
              streamingMessages: [],
            }),

          // Reset
          reset: () =>
            set({
              ...initialState,
              // Keep plan history flag
              hasPlanHistory: get().hasPlanHistory,
            }),
        }),
        {
          name: AI_PLANNER_STORE_NAME,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            // Only persist user preferences, not the plan data
            // Plan data is fetched from API (single source of truth)
            preferences: state.preferences,
            hasPlanHistory: state.hasPlanHistory,
            // Persist streaming messages to survive reloads during generation
            streamingMessages: state.streamingMessages,
            // Persist streaming text (AI explanation) to display in overview
            streamingText: state.streamingText,
          }),
        }
      ),
      { name: 'AI Planner Store' }
    )
  )
);

export const useAIPlannerStore = createSelectors(aiPlannerStore);
